import { type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@ttab/elephant-ui'
import { cn } from '@ttab/elephant-ui/utils'
import { CheckIcon, PlusIcon } from '@ttab/elephant-ui/icons'
import { isBlankBlock } from '@/shared/templates/memos/lib/insertedDocument'
import type { InsertedMemoDocument } from '@/shared/templates/memos/lib/insertedDocument'
import type { InsertTarget } from '../lib/insertTarget'

/** The day's published PMs and what inserting one of them does. */
export interface Insertable {
  documents: InsertedMemoDocument[]
  /** The ids of the PMs already folded in. */
  insertedIds: Set<string>
  target: InsertTarget
  onInsert: (document: InsertedMemoDocument, blockId: string) => void
  onRemove: (documentId: string) => void
}

/**
 * One of the day's PMs: a button that inserts it, asking which block first
 * where the template pins none and more than one is left. Once in, the same
 * button takes it back out.
 */
const InsertableDocument = ({
  document,
  isInserted,
  target,
  onInsert,
  onRemove
}: {
  document: InsertedMemoDocument
  isInserted: boolean
  target: InsertTarget
  onInsert: (blockId: string) => void
  onRemove: () => void
}): JSX.Element => {
  const { t } = useTranslation()

  const button = (onClick?: () => void) => (
    <Button
      variant='ghost'
      size='xs'
      className={cn(
        'max-w-full justify-start gap-1 px-2 font-normal',
        isInserted && 'text-muted-foreground'
      )}
      aria-label={isInserted
        ? t('memo:actions.removeInsertedDocument', { title: document.title })
        : undefined}
      onClick={onClick}
    >
      {isInserted
        ? <CheckIcon size={14} strokeWidth={1.75} />
        : <PlusIcon size={14} strokeWidth={1.75} />}
      <span className='truncate'>
        {isInserted
          ? document.title
          : t('memo:actions.insertDocument', { title: document.title })}
      </span>
    </Button>
  )

  if (isInserted) {
    return button(onRemove)
  }

  if (target.kind === 'block') {
    return button(() => onInsert(target.blockId))
  }

  if (target.blocks.length === 1) {
    return button(() => onInsert(target.blocks[0].id))
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={button()} />

      <DropdownMenuContent align='start'>
        <DropdownMenuLabel>{t('memo:labels.chooseBlock')}</DropdownMenuLabel>
        {target.blocks.map((block) => (
          <DropdownMenuItem
            key={block.id}
            onClick={() => onInsert(block.id)}
          >
            {block.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * The PMs already published today, to fold into this one whole - the morning
 * PM restating the night's five.
 *
 * Into a pinned block, a PM is only ever offered for inserting: once in, it is
 * taken out again with its own cross in the preview, right below. Without one,
 * the list stays put and its titles take an inserted PM back out.
 *
 * `inline` drops the visible heading, for a block header whose label already
 * says where the PM goes.
 */
export const InsertableDocuments = ({ insertable, inline = false }: {
  insertable: Insertable
  inline?: boolean
}): JSX.Element | null => {
  const { t } = useTranslation()
  const { documents, insertedIds, target, onInsert, onRemove } = insertable

  const offered = target.kind === 'block'
    ? documents.filter((document) => !insertedIds.has(document.id))
    : documents

  if (!offered.length) {
    return null
  }

  return (
    <section
      aria-label={t('memo:labels.insertable')}
      className={cn('flex items-center gap-2 min-w-0', !inline && 'flex-wrap')}
    >
      {!inline && (
        <span className='text-sm text-muted-foreground'>{t('memo:labels.insertable')}</span>
      )}

      {/*
        Side by side rather than one per row: a busy day writes enough PMs for
        a column of them to push the preview down. One too long for the row is
        truncated rather than left to widen it.
      */}
      <ul className='flex flex-wrap gap-1 items-start min-w-0 max-w-full'>
        {offered.map((document) => (
          <li
            key={document.id}
            className='min-w-0 max-w-full outline-2 outline-gray-300 dark:outline-gray-700 rounded'
            title={document.blocks
              .filter((block) => !isBlankBlock(block))
              .map((block) => block.data.text)
              .toLocaleString()}
          >
            <InsertableDocument
              document={document}
              isInserted={insertedIds.has(document.id)}
              target={target}
              onInsert={(blockId) => onInsert(document, blockId)}
              onRemove={() => onRemove(document.id)}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}
